<script setup lang="ts">
import { computed, defineProps, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { useRoute, useRouter } from 'vue-router';
import shared from '@/views/manage-content/media/shared';
import store from '@/store';

const { removeItemGetConfirm, removeSticker, approveSticker } = shared();

const route = useRoute();
const router = useRouter();
// Reactive: the editor can switch stickers without remounting this component
const item = computed(() => store.getters['sticker/GET_STICKER']);
const isOrgAdmin = computed(() => store.getters['users/isOrgAdmin']);
const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

// Loading states for each button
const isDeleteLoading = ref(false);
const isDenyLoading = ref(false);
const isApproveLoading = ref(false);
const isRestoreLoading = ref(false);

const props = defineProps<{
  isEditPage: boolean;
  isAdmin: boolean;
  stickerType: string;
  isActive: boolean;
  fetchSticker: () => Promise<void>;
  onSaveBeforeStatusChange?: () => Promise<void>;
}>();

const { isEditPage, fetchSticker, onSaveBeforeStatusChange } = props;

function removeItem() {
  if (isDeleteLoading.value) return;

  removeItemGetConfirm(async () => {
    isDeleteLoading.value = true;
    try {
      await removeSticker(item.value.id);
      router.push('/manage-content/media/stickers');
    } finally {
      isDeleteLoading.value = false;
    }
  });
}

const STATUS_MESSAGES: Record<string, string> = {
  Active: 'Sticker approved',
  Archived: 'Sticker archived',
  Pending: 'Sticker restored and sent for approval',
};

/**
 * Org admins restore an archived sticker to Pending, so it goes back through approval
 * (Approve stays super-admin only).
 */
async function restoreItem() {
  if (isRestoreLoading.value) return;
  isRestoreLoading.value = true;
  try {
    await changeStatus('Pending');
  } catch (error) {
    console.error('Error restoring sticker:', error);
    ElMessage.error(errorMessage(error, 'Could not restore the sticker'));
  } finally {
    isRestoreLoading.value = false;
  }
}

function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { message?: string })?.message;
  return message ? `${fallback}: ${message}` : fallback;
}

async function changeStatus(status: string) {
  // Save changes first if the save function is provided
  if (onSaveBeforeStatusChange) {
    await onSaveBeforeStatusChange();
  }
  await approveSticker({ id: String(route.params.id), status });
  await fetchSticker();
  ElMessage.success(STATUS_MESSAGES[status] || 'Saved');
}

async function publishItem(opt) {
  if (opt === false && isDenyLoading.value) return;
  if (opt === true && isApproveLoading.value) return;

  let status = 'Active';
  if (opt === false) {
    status = 'Archived';
    isDenyLoading.value = true;
  } else {
    isApproveLoading.value = true;
  }

  try {
    await changeStatus(status);
  } catch (error) {
    console.error('Error in publishItem:', error);
    ElMessage.error(errorMessage(error, `Could not ${opt ? 'approve' : 'archive'} the sticker`));
  } finally {
    if (opt === false) {
      isDenyLoading.value = false;
    } else {
      isApproveLoading.value = false;
    }
  }
}

const redirectToStickersPage = async () => {
  if (isOrgAdmin.value) {
    if (selectedOrganization.value && selectedOrganization.value && selectedOrganization.value.id) {
      return await router.push(`/manage/orgs/${selectedOrganization.value.id}/stickers`);
    }
    return router.push('/manage/orgs');
  }
  return router.push('/manage-content/media/stickers');
};

</script>

<template>
  <article class="c-breadcrumb-actions">
    <section class="c-add-sticker__breadcrumb" @click="redirectToStickersPage">
      <fai height="24px" icon="arrow-left" type="fas" width="24px" class="c-breadcrumb-actions__back-arrow" />
      <h1 class="c-breadcrumb-actions__title">{{ isEditPage ? 'Edit ' : 'Add ' }}Sticker</h1>
    </section>

    <section v-if="isEditPage && (isAdmin || isOrgAdmin)" class="c-breadcrumb-actions__buttons">
      <el-button
        v-if="isAdmin"
        type="danger"
        plain
        @click="removeItem"
        class="has-fai"
        :disabled="isDeleteLoading"
        :loading="isDeleteLoading"
      >
        <fai type="fal" icon="trash" class="size-18" />
        <span class="min-w-110 d-inline-block">Delete</span>
      </el-button>

      <el-button
        v-if="isAdmin && item.status !== 'Active'"
        type="success"
        :style="{ color: '#1EBEAF' }"
        @click="publishItem(true)"
        class="has-fai"
        :disabled="isApproveLoading"
        :loading="isApproveLoading"
      >
        <fai type="fal" icon="check-circle" class="size-18" style="color: #fff" />
        <span class="min-w-110 d-inline-block text-white">Approve</span>
      </el-button>

      <el-button
        v-if="item.status === 'Active'"
        type="danger"
        @click="publishItem(false)"
        class="has-fai"
        :disabled="isDenyLoading"
        :loading="isDenyLoading"
      >
        <fai type="fal" icon="times-circle" class="size-18" />
        <span class="min-w-110 d-inline-block">Archive</span>
      </el-button>

      <el-button
        v-if="!isAdmin && item.status === 'Archived'"
        type="primary"
        plain
        @click="restoreItem"
        class="has-fai"
        :disabled="isRestoreLoading"
        :loading="isRestoreLoading"
      >
        <fai type="fal" icon="redo" class="size-18" />
        <span class="min-w-110 d-inline-block">Restore</span>
      </el-button>
    </section>

  </article>
</template>

<style scoped lang="scss">
.c-breadcrumb-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__back-arrow {
    color: #111827;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateX(-2px);
    }
  }

  &__title {
    color: #111827;
    font-size: 1.875rem;
    margin: 0 0 0 12px;
    font-weight: 600;
    line-height: 1.2;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    &__title { font-size: 1.5rem; }

    // Delete / Approve (or Archive) share one row
    &__buttons {
      display: flex;
      gap: 8px;

      .el-button {
        flex: 1 1 0;
        min-width: 0;
        margin: 0;
      }

      .min-w-110 { min-width: 0 !important; }
    }
  }
}
</style>
